/**
 * The tenant Whitelabel tab (SASADM-0165 over SASADM-0145). The tenant's white-label
 * hosts are one stored domain set in saas-auth (SASATH-0297), read and written through
 * `apiAdmin.whitelabelDomain` and edited in `WhitelabelDomainPanel`. This screen:
 *   - loads that domain next to the hostnames list;
 *   - pairs each SYSTEM URL with its white-label URL from the domain set (matrix);
 *   - lists the OPERATOR rows that are NOT in the domain set under "Other hostnames".
 * The domain panel is the only way to add white-label names.
 *
 * SASADM-0171 — each APP row of the URL table can carry a custom hostname: a standalone host
 * (an OPERATOR APP row on another domain; saas-auth "standalone tenant hostnames", live routes),
 * set / changed / reverted through `CustomHostnameDialog`. The oldest standalone APP row per app
 * shows in that app's row with its DNS state and Check DNS; every other standalone row — and an
 * OPERATOR row on the white-label domain outside the set — shows under "Other hostnames"
 * (Check DNS, Change app, Remove). No other host type is added from the UI.
 *
 * SASADM-0173 (SASATH-0390 §4) — an Other-hostnames APP row can 308-redirect to a live APP row of
 * the tenant (Redirect to); a redirecting row is never an app's custom hostname. Add hostname
 * (`AddHostnameDialog`) adds a further standalone APP row, optionally redirecting.
 *
 * SASADM-0170 (SASATH-0387) — it also loads the tenant's app override rows once: they feed the
 * App builds panel (super-admin only) and mark a turned-off public app's URLs as disabled. A
 * deploying write whose routing deploy is REJECTED / FAILED shows a banner, never success.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { useEffect, useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError, redirectSourceIds } from '@/api/errors';
import { anyHasKey } from '@/api/featureDetect';
import { isDeployProblem } from '@/api/routingDeploy';
import type {
  AppOverrideDto,
  CaptchaWidgetDto,
  DeployRoutingResponse,
  HostnameDto,
  HostnameType,
  ProductAppDto,
  RoutingReadiness,
  RoutingDeployOutcome,
  RoutingReadinessHostname,
  TenantRoutingDeployResponse,
  WhitelabelDomainDto,
  WhitelabelHostnameDto,
} from '@/api/types';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HostUrl } from '@/components/HostUrl';
import { isClickableHostType } from '@/components/hostUrlRules';
import { HeldBackConflictNotice } from '@/components/HeldBackConflictNotice';
import { RedirectTargetNotice } from '@/components/RedirectTargetNotice';
import { RoutingDeployBanner } from '@/components/RoutingDeployBanner';
import { DISABLED_TEXT_SX } from '@/components/disabledStyle';
import { useCurrentOperator } from '@/auth/AuthContext';
import type { TenantWorkspaceLoaderData } from './TenantWorkspace';
import { WhitelabelDomainPanel } from './WhitelabelDomainPanel';
import { AppBuildsPanel, type AppOverridesLoadStatus } from './AppBuildsPanel';
import { CustomHostnameDialog } from './CustomHostnameDialog';
import { AddHostnameDialog } from './AddHostnameDialog';
import { TYPE_ORDER, fmtDate, isOnDomain, whitelabelHostKey } from './whitelabelShared';

type CopyFunction = (
  key: string,
  fallback?: string,
  values?: Record<string, string | number>,
) => string;

// SASADM-0170 — a failed routing deploy; the hostname writes (remove, custom hostname) offer the tenant deploy
// from the banner, the header deploys (which ARE the deploy) offer nothing.
interface DeployFailureState {
  outcome: RoutingDeployOutcome;
  offerTenantDeploy: boolean;
}

interface MatrixRow {
  key: string;
  hostnameType: HostnameType;
  appId: number | null;
  appSlug: string | null;
  system: HostnameDto | null;
  whitelabel: WhitelabelHostnameDto | null;
  // SASADM-0178 — any SYSTEM row of the group is pinned (a pinned apex shares its app's group).
  pinned: boolean;
}

// `appId: null` is documented (types.ts) for "legacy/unassigned APP rows" — plural, so a
// tenant can carry more than one. Such a row is definitionally unpairable (there is no
// appId to pair by), so each keys on its own id rather than sharing a single 'APP:null'
// bucket, where a second row would silently overwrite the first's slot.
function matrixKey(row: HostnameDto): string {
  if (row.hostnameType !== 'APP') return row.hostnameType;
  return row.appId != null ? `APP:${row.appId}` : `APP:id:${row.id}`;
}

// SYSTEM rows key by (type, appId); each white-label domain host keys the same way
// (`whitelabelHostKey`) and fills that group's `whitelabel` slot. A white-label host that
// pairs with no SYSTEM row still renders its own row (system cells blank) — it is the
// customer's configured URL, not something to hide.
function buildMatrixRows(systemRows: HostnameDto[], wlHosts: WhitelabelHostnameDto[]): MatrixRow[] {
  const groups = new Map<string, MatrixRow>();
  for (const row of systemRows) {
    const key = matrixKey(row);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        hostnameType: row.hostnameType,
        appId: row.appId ?? null,
        appSlug: null,
        system: null,
        whitelabel: null,
        pinned: false,
      };
      groups.set(key, group);
    }
    group.system = row;
    if (row.appPinned === true) group.pinned = true;
    if (group.appSlug == null && row.appSlug) group.appSlug = row.appSlug;
  }
  for (const host of wlHosts) {
    const key = whitelabelHostKey(host);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        hostnameType: host.hostnameType,
        appId: host.appId,
        appSlug: null,
        system: null,
        whitelabel: null,
        pinned: false,
      };
      groups.set(key, group);
    }
    group.whitelabel = host;
    if (group.appSlug == null && host.appSlug) group.appSlug = host.appSlug;
  }
  return [...groups.values()].sort(
    (a, b) =>
      TYPE_ORDER.indexOf(a.hostnameType) - TYPE_ORDER.indexOf(b.hostnameType) ||
      (a.appSlug ?? '').localeCompare(b.appSlug ?? ''),
  );
}

// Plain text, no chip. Fallback order for an APP row: resolved appName → appSlug → bare
// type label (an app can be deleted while its hostname survives, and appId is null for
// legacy/unpaired rows — neither renders "undefined"). White-label hosts carry no appName.
function appLabel(group: MatrixRow, t: CopyFunction): string {
  const typeText = t(`hostnames.types.${group.hostnameType}`, group.hostnameType);
  if (group.hostnameType !== 'APP') return typeText;
  const name = group.system?.appName ?? group.appSlug ?? null;
  return name ? `${typeText}: ${name}` : typeText;
}

// SASADM-0178 (SASATH-0395 §3) — a main address re-pointed to a non-primary app is pinned.
function PinnedChip() {
  const { t } = useCopy();
  return (
    <Tooltip
      title={t(
        'hostnames.pinned.tooltip',
        "Pointed at this app by an operator — Make primary won't move it. Point it at the primary app to unpin.",
      )}
    >
      <Chip size="small" variant="outlined" label={t('hostnames.pinned.chip', 'Pinned')} />
    </Tooltip>
  );
}

// SASADM-0171 — oldest first (createdAt, then id): an app's first standalone APP row is the
// custom hostname its URL-table row shows.
function byAge(a: HostnameDto, b: HostnameDto): number {
  return a.createdAt.localeCompare(b.createdAt) || a.id - b.id;
}

export function TenantHostnames() {
  const data = useRouteLoaderData('tenant-workspace') as TenantWorkspaceLoaderData;
  const tenant = data.tenant;
  const { t } = useCopy();
  const snack = useSnackbar();
  const [rows, setRows] = useState<HostnameDto[] | null>(null);
  // SASADM-0165 — the stored white-label domain (SASATH-0297). `undefined` loading,
  // `null` load failed (membership then unknown: no row is classified as other/custom).
  const [wlDomain, setWlDomain] = useState<WhitelabelDomainDto | null | undefined>(undefined);
  // Spec 0042 — routing readiness gates the in-app "Deploy routing changes" button.
  const [readiness, setReadiness] = useState<RoutingReadiness | null>(null);
  const [readinessLoaded, setReadinessLoaded] = useState(false);
  // Spec 0086 — the tenant's Turnstile widget state gates the "Sync Turnstile
  // hostnames" button. Fetched tolerantly: any failure leaves `widget` null.
  const [widget, setWidget] = useState<CaptchaWidgetDto | null>(null);
  const [clearTarget, setClearTarget] = useState<HostnameDto | null>(null);
  const [clearBusy, setClearBusy] = useState(false);
  // SASADM-0170 (SASATH-0387 §3A) — the tenant's PUBLIC app override rows, loaded once here:
  // they drive the App builds panel and the disabled marking of the matrix.
  const [appOverrides, setAppOverrides] = useState<AppOverrideDto[] | undefined>(undefined);
  const [overridesStatus, setOverridesStatus] = useState<AppOverridesLoadStatus>('loading');
  // SASADM-0170 — a deploying write whose routing deploy failed (REJECTED / FAILED). One source
  // of truth for the header deploys and the hostname writes; rendered by the hook.
  const [deployFailure, setDeployFailure] = useState<DeployFailureState | null>(null);
  // SASADM-0171 — the custom-hostname dialog's row (kept while it closes), Check DNS per row id,
  // and the product's FRONTEND apps for "Other hostnames" → Change app (loaded once, on need).
  const [customGroup, setCustomGroup] = useState<MatrixRow | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [dnsBusy, setDnsBusy] = useState<ReadonlySet<number>>(new Set());
  const [appsCache, setAppsCache] = useState<{
    productId: number;
    apps: ProductAppDto[];
  } | null>(null);
  // SASADM-0173 — the Add hostname dialog, and the "Other hostnames" redirect writes per row id.
  const [addOpen, setAddOpen] = useState(false);
  const [redirectBusy, setRedirectBusy] = useState<ReadonlySet<number>>(new Set());
  // SASADM-0176 (SASATH-0391/0392) — a hostname remove refused because others redirect to it.
  const [redirectTargetErr, setRedirectTargetErr] = useState<ApiError | null>(null);
  const operator = useCurrentOperator();

  async function refresh() {
    try {
      setRows(await apiAdmin.hostnames.list(tenant.id));
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
      setRows([]);
    }
    void refreshReadiness();
    void refreshWidget();
    void refreshDomain();
    void refreshOverrides();
  }

  async function refreshOverrides() {
    try {
      setAppOverrides(await apiAdmin.appOverrides.list(tenant.id));
      setOverridesStatus('ok');
    } catch (err) {
      setAppOverrides([]);
      const status = err instanceof ApiError ? err.status : null;
      setOverridesStatus(status === 403 ? 'forbidden' : status === 404 ? 'unavailable' : 'error');
    }
  }

  async function refreshDomain() {
    try {
      setWlDomain(await apiAdmin.whitelabelDomain.get(tenant.id));
    } catch {
      setWlDomain(null);
    }
  }

  async function refreshReadiness() {
    const r = await apiAdmin.routing.routingReadiness(tenant.id);
    setReadiness(r);
    setReadinessLoaded(true);
  }

  // Tolerate any error → leave `widget` null. A 403 for a non-SUPER_ADMIN operator
  // must NOT break the screen.
  async function refreshWidget() {
    try {
      setWidget(await apiAdmin.captcha.getWidget(tenant.id));
    } catch {
      setWidget(null);
    }
  }

  useEffect(() => {
    void (async () => {
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id]);

  // Removes an "Other hostnames" row (an OPERATOR row outside the domain set).
  async function handleClear() {
    if (!clearTarget) return;
    setClearBusy(true);
    setRedirectTargetErr(null);
    try {
      const outcome = await apiAdmin.hostnames.delete(tenant.id, clearTarget.id);
      if (isDeployProblem(outcome)) {
        setDeployFailure({ outcome, offerTenantDeploy: true });
      } else {
        snack.success(t('snackbar.hostnameRemoved', 'Hostname removed.'));
      }
      setClearTarget(null);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'HOSTNAME_IS_REDIRECT_TARGET') {
        setRedirectTargetErr(err);
      } else if (err instanceof ApiError) {
        snack.error(t(`errors.codes.${err.code}`, err.code));
      }
      setClearTarget(null);
    } finally {
      setClearBusy(false);
    }
  }

  // SASADM-0176 — the redirect sources a HOSTNAME_IS_REDIRECT_TARGET lists, as this tenant's
  // hostnames (an id not among `rows` is left out).
  function redirectSources(err: ApiError): string[] {
    const byId = new Map((rows ?? []).map((r) => [r.id, r.hostname]));
    return redirectSourceIds(err).flatMap((id) => byId.get(id) ?? []);
  }

  // A row belongs to the domain set iff its id is in `wlDomain.hostnames` (`HostnameDto`
  // has no domain-set flag). With the domain unknown (load failed) nothing is classified.
  const wlIds = new Set((wlDomain?.hostnames ?? []).map((h) => h.id));
  const systemRows = (rows ?? []).filter((r) => r.source === 'SYSTEM');
  const matrixRows = rows ? buildMatrixRows(systemRows, wlDomain?.hostnames ?? []) : null;
  const wlDomainName = wlDomain?.configured ? wlDomain.domain : null;
  const outsideSet = wlDomain
    ? (rows ?? []).filter((r) => r.source === 'OPERATOR' && !wlIds.has(r.id))
    : [];
  /** Standalone = OPERATOR rows not in the domain set and not on the white-label domain (handoff §1.1). */
  const standaloneRows = outsideSet.filter((r) => !isOnDomain(r.hostname, wlDomainName));
  // SASADM-0171 — each APP table row's custom hostname: its app's oldest standalone APP row.
  // SASADM-0173 — a redirecting row never is (it only forwards); it stays in Other hostnames.
  const tableAppIds = new Set(
    (matrixRows ?? [])
      .filter((g) => g.hostnameType === 'APP' && g.appId != null)
      .map((g) => g.appId),
  );
  const customByAppId = new Map<number, HostnameDto>();
  for (const r of [...standaloneRows].sort(byAge)) {
    if (r.hostnameType !== 'APP' || r.appId == null || !tableAppIds.has(r.appId)) continue;
    if (r.redirectToHostnameId != null) continue;
    if (!customByAppId.has(r.appId)) customByAppId.set(r.appId, r);
  }
  const customIds = new Set([...customByAppId.values()].map((r) => r.id));
  // Every other standalone row, plus white-label-domain rows outside the set (the old Set-URL).
  const otherRows = outsideSet.filter((r) => !customIds.has(r.id));
  // SASADM-0173 — Add hostname picks an app from the same list.
  const needsFrontendApps = addOpen || otherRows.some((r) => r.hostnameType === 'APP');

  // Keyed by product: the tab isn't remounted per tenant, so another product's list never shows.
  const frontendApps =
    appsCache && appsCache.productId === tenant.productId ? appsCache.apps : null;

  useEffect(() => {
    const productId = tenant.productId;
    if (!needsFrontendApps || productId == null || appsCache?.productId === productId) return;
    let cancelled = false;
    void (async () => {
      let apps: ProductAppDto[] = [];
      try {
        apps = (await apiAdmin.productApps.list(productId)).filter((a) => a.kind === 'FRONTEND');
      } catch {
        // No select on a failed load; Remove and Check DNS still work.
      }
      if (!cancelled) setAppsCache({ productId, apps });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsFrontendApps, tenant.productId]);

  // SASADM-0171 — Check DNS on a custom / other hostname: verify-dns persists a match.
  async function handleCheckDns(row: HostnameDto) {
    setDnsBusy((s) => new Set(s).add(row.id));
    try {
      // SASADM-0172 (SASATH-0388 §3C.1) — `routingDeploy` is present only when this check flipped
      // an APP row to verified and so deployed it.
      const { check, routingDeploy } = await apiAdmin.hostnames.verifyDns(tenant.id, row.id);
      if (isDeployProblem(routingDeploy)) {
        setDeployFailure({ outcome: routingDeploy, offerTenantDeploy: true });
        await refresh();
      } else if (check.match) {
        snack.success(
          routingDeploy
            ? t(
                'hostnames.custom.verifiedDeployed',
                'DNS verified — routing deployed. The hostname is live.',
              )
            : t('snackbar.hostnameDnsVerified', 'DNS verified — hostname is now active.'),
        );
        await refresh();
      } else if (check.configured === false) {
        snack.info(
          t(
            'hostnames.custom.dnsNotConfigured',
            "DNS checking isn't configured on saas-auth (an ops task).",
          ),
        );
      } else {
        snack.info(
          t(
            'hostnames.custom.dnsPending',
            'DNS not found yet. It can take a few minutes after you create the CNAME.',
          ),
        );
      }
    } catch (err) {
      if (err instanceof ApiError) {
        snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
      }
    } finally {
      setDnsBusy((s) => {
        const next = new Set(s);
        next.delete(row.id);
        return next;
      });
    }
  }

  async function handleSetApp(row: HostnameDto, appId: number) {
    try {
      await apiAdmin.hostnames.setApp(tenant.id, row.id, appId);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) {
        snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
      }
    }
  }

  // SASADM-0173 (SASATH-0390 §4) — set or clear an Other-hostnames APP row's 308 redirect.
  async function handleSetRedirect(row: HostnameDto, targetHostnameId: number | null) {
    setRedirectBusy((s) => new Set(s).add(row.id));
    try {
      const updated = await apiAdmin.hostnames.setRedirect(tenant.id, row.id, targetHostnameId);
      if (isDeployProblem(updated.routingDeploy)) {
        setDeployFailure({ outcome: updated.routingDeploy, offerTenantDeploy: true });
      } else {
        snack.success(
          targetHostnameId === null
            ? t('hostnames.redirect.cleared', 'Redirect removed.')
            : t('hostnames.redirect.set', 'Redirect set.'),
        );
      }
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) {
        snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
      }
    } finally {
      setRedirectBusy((s) => {
        const next = new Set(s);
        next.delete(row.id);
        return next;
      });
    }
  }

  function handleAdded(
    created: HostnameDto,
    redirectOutcome: RoutingDeployOutcome | null | undefined,
    redirectError?: string,
  ) {
    if (redirectError) {
      snack.warning(
        t(
          'hostnames.add.redirectFailed',
          "Added {{host}}, but the redirect wasn't set: {{message}}. Set it from Other hostnames.",
          { host: created.hostname, message: redirectError },
        ),
      );
    }
    const failure = isDeployProblem(created.routingDeploy)
      ? created.routingDeploy
      : isDeployProblem(redirectOutcome)
        ? redirectOutcome
        : null;
    if (failure) {
      setDeployFailure({ outcome: failure, offerTenantDeploy: true });
    } else if (!redirectError) {
      snack.success(
        t(
          'hostnames.add.added',
          'Added. Create the CNAME, then press Check DNS — it puts the hostname live.',
        ),
      );
    }
    void refresh();
  }

  function handleCustomChanged(
    outcome: RoutingDeployOutcome | null,
    kind: 'set' | 'reverted',
    oldKept?: string,
  ) {
    if (oldKept) {
      snack.warning(
        t(
          'hostnames.custom.oldKept',
          "The new hostname was added, but {{old}} couldn't be removed. Remove it under Other hostnames.",
          { old: oldKept },
        ),
      );
    }
    if (isDeployProblem(outcome)) {
      setDeployFailure({ outcome, offerTenantDeploy: true });
    } else if (oldKept) {
      // The warning above already says the new hostname was added.
    } else if (kind === 'reverted') {
      snack.success(t('hostnames.custom.reverted', 'Reverted to the white-label URL.'));
    } else if (outcome) {
      snack.success(t('hostnames.custom.added', 'Added. Create the CNAME, then press Check DNS.'));
    } else {
      snack.info(
        t(
          'hostnames.custom.addedDeploy',
          'Added. Create the CNAME, then press Check DNS — it puts the hostname live.',
        ),
      );
    }
    void refresh();
  }

  function renderCheckDns(row: HostnameDto) {
    const busy = dnsBusy.has(row.id);
    return (
      <Button
        size="small"
        disabled={busy}
        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        onClick={() => void handleCheckDns(row)}
      >
        {t('hostnames.custom.checkDns', 'Check DNS')}
      </Button>
    );
  }

  function renderDnsState(row: HostnameDto) {
    if (row.dnsVerifiedAt) {
      return (
        <Typography variant="caption" sx={{ color: 'success.main' }}>
          {t('hostnames.verifiedOn', 'Verified {{date}}', { date: fmtDate(row.dnsVerifiedAt) })}
        </Typography>
      );
    }
    return (
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('hostnames.wl.notVerified', 'Not verified')}
        </Typography>
        {renderCheckDns(row)}
      </Stack>
    );
  }

  const dnsRecords = wlDomain?.dnsRecords ?? [];
  const canCopyDns = Boolean(
    wlDomain?.configured && wlDomain.dnsConfigured && dnsRecords.length > 0,
  );

  async function handleCopyDnsInstructions() {
    if (!canCopyDns) return;
    const records = dnsRecords.map((r) => `  ${r.host}    CNAME    ${r.target}`).join('\n');
    const text = `DNS records for ${tenant.name}\n\nCreate the following CNAME records:\n\n${records}\n\nThese must all resolve before the domains go live.`;
    try {
      await navigator.clipboard.writeText(text);
      snack.success(t('hostnames.summary.dnsCopied', 'DNS instructions copied.'));
    } catch {
      // Clipboard access can be denied by browser policy; do not throw into render.
    }
  }

  const {
    platformButton,
    tenantButton,
    feedback: routingFeedback,
    deployTenant,
    tenantDeploy,
  } = useRoutingDeploy({
    readiness,
    readinessLoaded,
    tenantId: tenant.id,
    deployFailure,
    setDeployFailure,
  });
  const { syncButton } = useCaptchaSync({
    tenantId: tenant.id,
    widget,
    onSynced: () => void refreshWidget(),
  });

  // SASADM-0170 — an APP host is disabled iff its app's public app is turned off for this tenant.
  const disabledAppIds = new Set(
    (appOverrides ?? []).filter((r) => !r.enabled).map((r) => r.appId),
  );
  const isAppOff = (hostnameType: HostnameType, appId: number | null | undefined) =>
    hostnameType === 'APP' && appId != null && disabledAppIds.has(appId);
  // SASADM-0173 (SASATH-0390 §4) — the APP rows a redirect may target: verified, not themselves a
  // redirect, app not turned off. The server re-checks liveness.
  const liveAppTargets = (rows ?? []).filter(
    (r) =>
      r.hostnameType === 'APP' &&
      r.dnsVerified &&
      r.redirectToHostnameId == null &&
      !(r.appId != null && disabledAppIds.has(r.appId)),
  );
  // SASADM-0174 — until SASATH-0390 deploys, saas-auth omits the key and 404s the redirect route.
  const supportsRedirects = anyHasKey(rows, 'redirectToHostnameId');

  function renderMatrixRow(group: MatrixRow) {
    const label = appLabel(group, t);
    const wl = group.whitelabel;
    const off = isAppOff(group.hostnameType, group.appId);
    // SASADM-0171 — APP groups with an app id get the custom-hostname override.
    const canCustomize = group.hostnameType === 'APP' && group.appId != null;
    const custom =
      canCustomize && group.appId != null ? (customByAppId.get(group.appId) ?? null) : null;
    const customLabel = t('hostnames.custom.open', 'Custom hostname for {{app}}', { app: label });
    return (
      <TableRow key={group.key}>
        <TableCell>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={off ? DISABLED_TEXT_SX : undefined}>
              {label}
            </Typography>
            {group.pinned && <PinnedChip />}
          </Stack>
        </TableCell>
        <TableCell>
          {group.system && (
            <HostUrl
              hostname={group.system.hostname}
              clickable={isClickableHostType(group.hostnameType)}
              disabled={off}
            />
          )}
        </TableCell>
        <TableCell>
          {group.system?.dnsVerifiedAt ? (
            <Typography variant="caption" sx={{ color: 'success.main' }}>
              {t('hostnames.verifiedOn', 'Verified {{date}}', {
                date: fmtDate(group.system.dnsVerifiedAt),
              })}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell>
          {canCustomize ? (
            <Stack
              direction="row"
              sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}
            >
              <Box sx={{ minWidth: 0 }}>
                {custom ? (
                  <>
                    <Stack
                      direction="row"
                      sx={{ alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
                    >
                      <HostUrl hostname={custom.hostname} clickable disabled={off} />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t('hostnames.custom.chip', 'Custom')}
                      />
                    </Stack>
                    {wl && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block', wordBreak: 'break-all' }}
                      >
                        {t('hostnames.custom.alsoWl', 'Also: {{url}}', {
                          url: `https://${wl.hostname}`,
                        })}
                      </Typography>
                    )}
                  </>
                ) : (
                  wl && <HostUrl hostname={wl.hostname} clickable disabled={off} />
                )}
              </Box>
              <Tooltip title={customLabel}>
                <IconButton
                  size="small"
                  aria-label={customLabel}
                  sx={{ color: 'text.secondary' }}
                  onClick={() => {
                    setCustomGroup(group);
                    setCustomOpen(true);
                  }}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            wl && (
              <HostUrl
                hostname={wl.hostname}
                clickable={isClickableHostType(group.hostnameType)}
                disabled={off}
              />
            )
          )}
        </TableCell>
        <TableCell>
          {custom ? (
            renderDnsState(custom)
          ) : wl?.dnsVerifiedAt ? (
            <Typography variant="caption" sx={{ color: 'success.main' }}>
              {t('hostnames.verifiedOn', 'Verified {{date}}', {
                date: fmtDate(wl.dnsVerifiedAt),
              })}
            </Typography>
          ) : wl ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('hostnames.wl.notVerified', 'Not verified')}
            </Typography>
          ) : null}
        </TableCell>
      </TableRow>
    );
  }

  function renderOtherRow(row: HostnameDto) {
    const typeText = t(`hostnames.types.${row.hostnameType}`, row.hostnameType);
    const isApp = row.hostnameType === 'APP';
    const appName = row.appName ?? row.appSlug ?? null;
    const removeLabel = t('hostnames.other.remove', 'Remove {{hostname}}', {
      hostname: row.hostname,
    });
    const changeAppLabel = t('hostnames.other.changeApp', 'Change app for {{hostname}}', {
      hostname: row.hostname,
    });
    const off = isAppOff(row.hostnameType, row.appId);
    // SASADM-0173 — a redirecting row only forwards: plain URL, then "→ target (308)" + chip.
    const redirecting = row.redirectToHostname != null;
    const redirectLabel = t('hostnames.redirect.aria', 'Redirect {{host}} to', {
      host: row.hostname,
    });
    const redirectOptions = liveAppTargets.filter((r) => r.id !== row.id);
    // A current target that is no longer live still shows as the selected value.
    const staleTarget =
      row.redirectToHostnameId != null &&
      !redirectOptions.some((r) => r.id === row.redirectToHostnameId)
        ? row.redirectToHostnameId
        : null;
    return (
      <TableRow key={row.id}>
        <TableCell>
          <HostUrl
            hostname={row.hostname}
            clickable={!redirecting && isClickableHostType(row.hostnameType)}
            disabled={off}
          />
          {redirecting && (
            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                → {row.redirectToHostname} (308)
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={t('hostnames.redirect.chip', 'Redirect')}
              />
            </Stack>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2">{typeText}</Typography>
        </TableCell>
        <TableCell>
          {isApp && appName && (
            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={off ? DISABLED_TEXT_SX : undefined}>
                {appName}
              </Typography>
              {row.appPinned === true && <PinnedChip />}
            </Stack>
          )}
        </TableCell>
        <TableCell>{renderDnsState(row)}</TableCell>
        <TableCell align="right">
          <Stack direction="row" sx={{ justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
            {isApp && frontendApps && frontendApps.length > 0 && (
              <TextField
                select
                size="small"
                value={frontendApps.some((a) => a.id === row.appId) ? String(row.appId) : ''}
                onChange={(e) => void handleSetApp(row, Number(e.target.value))}
                sx={{ minWidth: 160 }}
                slotProps={{ htmlInput: { 'aria-label': changeAppLabel } }}
              >
                {frontendApps.map((a) => (
                  <MenuItem key={a.id} value={String(a.id)}>
                    {a.displayName}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {isApp && supportsRedirects && (
              <TextField
                select
                size="small"
                value={row.redirectToHostnameId != null ? String(row.redirectToHostnameId) : ''}
                onChange={(e) =>
                  void handleSetRedirect(row, e.target.value === '' ? null : Number(e.target.value))
                }
                disabled={redirectBusy.has(row.id)}
                sx={{ minWidth: 160 }}
                slotProps={{
                  htmlInput: { 'aria-label': redirectLabel },
                  select: { displayEmpty: true },
                }}
              >
                <MenuItem value="">{t('hostnames.redirect.none', 'No redirect')}</MenuItem>
                {staleTarget != null && (
                  <MenuItem value={String(staleTarget)}>{row.redirectToHostname}</MenuItem>
                )}
                {redirectOptions.map((r) => (
                  <MenuItem key={r.id} value={String(r.id)}>
                    {r.hostname}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Tooltip title={removeLabel}>
              <IconButton aria-label={removeLabel} onClick={() => setClearTarget(row)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
    );
  }

  const customApp = customGroup;
  const customHost = customApp?.appId != null ? (customByAppId.get(customApp.appId) ?? null) : null;
  const customCurrent = customHost ?? customApp?.whitelabel ?? customApp?.system ?? null;
  // Removing the tenant's only APP host gets one more sentence in the confirm.
  const clearsOnlyApp =
    clearTarget?.hostnameType === 'APP' &&
    !(rows ?? []).some((r) => r.hostnameType === 'APP' && r.id !== clearTarget.id);

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h2" component="h2">
          {t('hostnames.whitelabel.title', 'Whitelabel')}
        </Typography>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {platformButton}
          {tenantButton}
          {syncButton}
        </Stack>
      </Stack>
      {routingFeedback}
      {redirectTargetErr && (
        <RedirectTargetNotice
          err={redirectTargetErr}
          sourceHostnames={redirectSources(redirectTargetErr)}
          onClose={() => setRedirectTargetErr(null)}
        />
      )}
      <WhitelabelDomainPanel
        tenantId={tenant.id}
        domain={wlDomain}
        systemRows={systemRows}
        hostnames={rows ?? []}
        onChanged={() => void refresh()}
        deployTenant={deployTenant}
        tenantDeploy={tenantDeploy}
      />
      {operator?.isSuperAdmin && (
        <AppBuildsPanel
          tenantId={tenant.id}
          tenantSlug={tenant.slug}
          tenantName={tenant.name}
          rows={appOverrides ?? []}
          loadStatus={overridesStatus}
          onChanged={() => void refresh()}
          deployTenant={deployTenant}
          tenantDeploy={tenantDeploy}
        />
      )}
      {matrixRows === null ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>{t('common.loading', 'Loading')}</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-start' } }}
          >
            <Box>
              <Typography variant="h3" component="h3" sx={{ mb: 0.5 }}>
                {t('hostnames.summary.title', 'This tenant’s URLs')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t(
                  'hostnames.summary.help',
                  'Every hostname this tenant is reachable on. System URLs are provisioned by the platform; white-label URLs come from the tenant’s white-label domain.',
                )}
              </Typography>
            </Box>
            <Tooltip
              title={
                canCopyDns
                  ? ''
                  : !wlDomain?.configured
                    ? t(
                        'hostnames.summary.dnsNoDomain',
                        'This tenant has no white-label domain — nothing for the customer to publish.',
                      )
                    : t(
                        'hostnames.summary.dnsNoTarget',
                        'The platform DNS target isn’t available, so instructions would be incomplete.',
                      )
              }
            >
              <span>
                <Button
                  variant="outlined"
                  disabled={!canCopyDns}
                  onClick={() => void handleCopyDnsInstructions()}
                >
                  {t('hostnames.summary.dnsCopy', 'Copy DNS instructions')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
          {matrixRows.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('hostnames.empty', 'No hostnames yet.')}
            </Alert>
          ) : (
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small" aria-label={t('hostnames.summary.title', 'This tenant’s URLs')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('hostnames.type', 'Type')}</TableCell>
                    <TableCell>{t('hostnames.summary.columns.systemUrl', 'System URL')}</TableCell>
                    <TableCell>{t('hostnames.summary.columns.systemDns', 'System DNS')}</TableCell>
                    <TableCell>
                      {t('hostnames.summary.columns.whitelabelUrl', 'Whitelabel URL')}
                    </TableCell>
                    <TableCell>
                      {t('hostnames.summary.columns.whitelabelDns', 'Whitelabel DNS')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{matrixRows.map(renderMatrixRow)}</TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
      {/* SASADM-0173 — always rendered once loaded, so Add hostname is reachable. */}
      {matrixRows !== null && (
        <Paper variant="outlined" sx={{ p: 2.5, mt: 3 }}>
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}
          >
            <Typography variant="h3" component="h3">
              {t('hostnames.other.title', 'Other hostnames')}
            </Typography>
            <Button size="small" onClick={() => setAddOpen(true)}>
              {t('hostnames.other.add', 'Add hostname')}
            </Button>
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {t(
              'hostnames.other.help',
              "Extra hostnames for this tenant on another domain, e.g. a marketing site at www.example.ai while the apps and sign-in stay on the white-label domain. Each one needs its own CNAME and DNS check. They aren't part of the white-label domain's daily re-check. Pages on another domain can link to sign-in, but can't silently tell who is signed in (browsers block that cookie across domains).",
            )}
          </Typography>
          {supportsRedirects ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {t(
                'hostnames.redirect.hint',
                'Redirect: visitors to a hostname are sent to its target, keeping the path. Use it for www → bare domain. A bare domain needs CNAME flattening or an A record at your DNS provider.',
              )}
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 2 }}>
              {t(
                'hostnames.redirect.needsNewerAuth',
                'Redirects appear once saas-auth is updated.',
              )}
            </Typography>
          )}
          {otherRows.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('hostnames.other.empty', 'No other hostnames.')}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" aria-label={t('hostnames.other.title', 'Other hostnames')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('hostnames.hostname', 'Hostname')}</TableCell>
                    <TableCell>{t('hostnames.type', 'Type')}</TableCell>
                    <TableCell>{t('hostnames.other.colApp', 'App')}</TableCell>
                    <TableCell>{t('hostnames.other.colDns', 'DNS')}</TableCell>
                    <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{otherRows.map(renderOtherRow)}</TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
      <AddHostnameDialog
        open={addOpen}
        tenantId={tenant.id}
        frontendApps={frontendApps ?? []}
        liveAppTargets={liveAppTargets}
        supportsRedirects={supportsRedirects}
        wlDomainName={wlDomainName}
        systemHostnames={systemRows.map((r) => r.hostname)}
        onClose={() => setAddOpen(false)}
        onAdded={handleAdded}
      />
      <ConfirmDialog
        open={Boolean(clearTarget)}
        title={t('hostnames.clear.confirmTitle', 'Remove custom hostname?')}
        body={
          clearsOnlyApp
            ? `${t(
                'hostnames.clear.confirmBody',
                'This removes {{hostname}} — the customer will no longer reach this tenant at that address.',
                { hostname: clearTarget?.hostname ?? '' },
              )} ${t('hostnames.other.onlyApp', "This is the tenant's only app hostname.")}`
            : t(
                'hostnames.clear.confirmBody',
                'This removes {{hostname}} — the customer will no longer reach this tenant at that address.',
                { hostname: clearTarget?.hostname ?? '' },
              )
        }
        severity="error"
        confirmLabel={t('hostnames.clear.confirmAction', 'Clear')}
        busy={clearBusy}
        onConfirm={() => void handleClear()}
        onCancel={() => setClearTarget(null)}
      />
      {customApp && customApp.appId != null && (
        <CustomHostnameDialog
          open={customOpen}
          tenantId={tenant.id}
          appId={customApp.appId}
          appLabel={appLabel(customApp, t)}
          currentUrl={customCurrent ? `https://${customCurrent.hostname}` : null}
          custom={customHost}
          wlDomainName={wlDomainName}
          systemHostnames={systemRows.map((r) => r.hostname)}
          onClose={() => setCustomOpen(false)}
          onChanged={handleCustomChanged}
          onRedirectTarget={(err) => {
            setCustomOpen(false);
            setRedirectTargetErr(err);
          }}
        />
      )}
    </Box>
  );
}

// Spec 0042 — the in-app routing-deploy control. The deploy is PLATFORM-WIDE
// (the render regenerates every tenant's routing from the DB in one serialized
// pass); the per-tenant `readiness` only gates when it's sensible to click from
// this screen — the copy never implies it affects only this tenant. The button
// is enabled solely on a positive `readiness.ready`; it stays inert when
// readiness is unavailable (endpoint absent pre-0046) or still loading.
//
// SASADM-0170 (SASATH-0387 §5.1) — a deploy whose `routingDeploy` is REJECTED / FAILED is not a
// success: no result Paper, no success toast; the lifted `deployFailure` banner renders instead.
function useRoutingDeploy({
  readiness,
  readinessLoaded,
  tenantId,
  deployFailure,
  setDeployFailure,
}: {
  readiness: RoutingReadiness | null;
  readinessLoaded: boolean;
  tenantId: number;
  deployFailure: DeployFailureState | null;
  setDeployFailure: (failure: DeployFailureState | null) => void;
}) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [deploying, setDeploying] = useState(false);
  const [result, setResult] = useState<DeployRoutingResponse | null>(null);
  const [deployingTenant, setDeployingTenant] = useState(false);
  const [tenantResult, setTenantResult] = useState<TenantRoutingDeployResponse | null>(null);
  // SASADM-0176 (SASATH-0392) — a 409 CADDY_HELD_BACK_HOST_CONFLICT from either header deploy.
  const [heldBackConflict, setHeldBackConflict] = useState<ApiError | null>(null);

  const unavailable = readinessLoaded && readiness === null;
  const ready = readiness?.ready === true;
  const blocking = readiness ? readiness.hostnames.filter((h) => h.blocking) : [];

  async function handleDeploy() {
    setDeploying(true);
    setHeldBackConflict(null);
    try {
      const res = await apiAdmin.routing.deployRouting();
      if (isDeployProblem(res.routingDeploy)) {
        setResult(null);
        setDeployFailure({ outcome: res.routingDeploy, offerTenantDeploy: false });
        return;
      }
      setDeployFailure(null);
      setResult(res);
      snack.success(t('routing.deploy.doneGlobalSnack', 'Routing deployed.'));
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'CADDY_HELD_BACK_HOST_CONFLICT') {
          setResult(null);
          setDeployFailure(null);
          setHeldBackConflict(err);
        } else if (err.code === 'CADDY_CONFIG_INVALID' || err.code === 'CADDY_RELOAD_FAILED') {
          snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
        } else if (err.code === 'DEPLOY_NOT_CONFIGURED' || err.code === 'DEPLOY_DISPATCH_FAILED') {
          snack.error(t(`errors.codes.${err.code}`, err.code));
        } else if (err.status === 403) {
          snack.error(
            t('errors.insufficientPermissions', "You don't have permission for this action."),
          );
        } else {
          snack.error(t(`errors.codes.${err.code}`, err.code));
        }
      }
    } finally {
      setDeploying(false);
    }
  }

  // Spec 0064 (saas-auth SASATH-0093) — per-tenant in-app deploy: renders + reloads
  // just this tenant's Caddy fragment. Shares the readiness gating with the global
  // button; CADDY_* failures surface the backend message inline.
  async function handleDeployTenant(): Promise<boolean> {
    setDeployingTenant(true);
    setHeldBackConflict(null);
    try {
      const res = await apiAdmin.routing.deployTenantRouting(tenantId);
      if (isDeployProblem(res.routingDeploy)) {
        setTenantResult(null);
        setDeployFailure({ outcome: res.routingDeploy, offerTenantDeploy: false });
        return false;
      }
      setDeployFailure(null);
      setTenantResult(res);
      snack.success(t('routing.deploy.doneTenantSnack', 'Tenant routing deployed.'));
      return true;
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'CADDY_HELD_BACK_HOST_CONFLICT') {
          setTenantResult(null);
          setDeployFailure(null);
          setHeldBackConflict(err);
        } else if (err.code === 'CADDY_CONFIG_INVALID' || err.code === 'CADDY_RELOAD_FAILED') {
          snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
        } else if (err.code === 'TENANT_NOT_FOUND') {
          snack.error(t('errors.codes.TENANT_NOT_FOUND', 'Tenant not found.'));
        } else if (err.status === 403) {
          snack.error(
            t('errors.insufficientPermissions', "You don't have permission for this action."),
          );
        } else {
          snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
        }
      }
      return false;
    } finally {
      setDeployingTenant(false);
    }
  }

  // Disabled-button tooltip explains why it's inert (MUI needs the <span> wrapper).
  const disabledReason = !readinessLoaded
    ? ''
    : unavailable
      ? t('routing.deploy.unavailable', "Routing deploy isn't available yet.")
      : !ready
        ? t(
            'routing.deploy.notReadyTooltip',
            "Some hostnames below are still pending; they won't be served until resolved.",
          )
        : '';

  const basePlatformButton = (
    <Button
      variant="contained"
      onClick={() => void handleDeploy()}
      disabled={unavailable || deploying}
      startIcon={deploying ? <CircularProgress size={16} color="inherit" /> : null}
    >
      {t('routing.deploy.button', 'Deploy Platform Routing')}
    </Button>
  );

  const baseTenantButton = (
    <Button
      variant="outlined"
      onClick={() => void handleDeployTenant()}
      disabled={unavailable || deployingTenant}
      startIcon={deployingTenant ? <CircularProgress size={16} color="inherit" /> : null}
    >
      {t('routing.deploy.tenantButton', 'Deploy this Tenant Routing')}
    </Button>
  );

  const platformButton = disabledReason ? (
    <Tooltip title={disabledReason}>
      <span>{basePlatformButton}</span>
    </Tooltip>
  ) : (
    basePlatformButton
  );
  const tenantButton = disabledReason ? (
    <Tooltip title={disabledReason}>
      <span>{baseTenantButton}</span>
    </Tooltip>
  ) : (
    baseTenantButton
  );

  const hasFeedback =
    unavailable ||
    (readiness != null && !ready && blocking.length > 0) ||
    result != null ||
    tenantResult != null ||
    deployFailure != null ||
    heldBackConflict != null;
  const feedback = hasFeedback ? (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      {heldBackConflict && (
        <HeldBackConflictNotice err={heldBackConflict} onClose={() => setHeldBackConflict(null)} />
      )}
      {deployFailure && (
        <RoutingDeployBanner
          outcome={deployFailure.outcome}
          currentTenantId={tenantId}
          onClose={() => setDeployFailure(null)}
          action={
            deployFailure.offerTenantDeploy ? (
              <Button
                color="inherit"
                size="small"
                disabled={unavailable || deployingTenant}
                startIcon={deployingTenant ? <CircularProgress size={16} color="inherit" /> : null}
                onClick={() => void handleDeployTenant()}
              >
                {t('routingDeploy.deployTenant', "Deploy this tenant's routing")}
              </Button>
            ) : undefined
          }
        />
      )}
      {unavailable && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            mt: 1.5,
          }}
        >
          {t('routing.deploy.unavailable', "Routing deploy isn't available yet.")}
        </Typography>
      )}
      {readiness && !ready && blocking.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('routing.deploy.blockingIntro', 'Pending before this tenant is go-live ready:')}
          </Typography>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
            {blocking.map((h) => (
              <Typography
                component="li"
                key={h.hostname}
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {h.hostname} — {blockingReason(h, t)}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      {result && (
        <Alert severity="success" sx={{ mt: 1.5 }}>
          {t(
            'routing.deploy.doneGlobal',
            'Routing deployed — {{tenants}} tenants, {{written}} fragments written, {{removed}} removed.',
            {
              tenants: result.tenants,
              written: result.fragmentsWritten,
              removed: result.filesRemoved,
            },
          )}{' '}
          {result.reloaded
            ? t('routing.deploy.reloaded', 'Caddy reloaded.')
            : t('routing.deploy.reloadSkipped', 'Caddy reload skipped.')}
        </Alert>
      )}
      {tenantResult && (
        <Alert severity="success" sx={{ mt: 1.5 }}>
          {tenantResult.file
            ? t('routing.deploy.doneTenant', 'Deployed this tenant — wrote {{file}}.', {
                file: tenantResult.file,
              })
            : t(
                'routing.deploy.doneTenantRemoved',
                'Deployed this tenant — no routable hosts, fragment removed.',
              )}{' '}
          {tenantResult.reloaded
            ? t('routing.deploy.reloaded', 'Caddy reloaded.')
            : t('routing.deploy.reloadSkipped', 'Caddy reload skipped.')}
        </Alert>
      )}
    </Paper>
  ) : null;

  return {
    platformButton,
    tenantButton,
    feedback,
    deployTenant: handleDeployTenant,
    tenantDeploy: { disabled: unavailable || deployingTenant, busy: deployingTenant },
  };
}

// Spec 0086 — "Sync Turnstile hostnames": pushes the tenant's CURRENT APP+LOGIN
// hostnames to its Cloudflare widget. A distinct action from the Caddy routing
// deploy above. Disabled (with a tooltip) until a widget is provisioned; on
// success re-reads the widget to refresh state.
function useCaptchaSync({
  tenantId,
  widget,
  onSynced,
}: {
  tenantId: number;
  widget: CaptchaWidgetDto | null;
  onSynced: () => void;
}) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [syncing, setSyncing] = useState(false);
  const provisioned = widget?.provisioned === true;

  async function handleSync() {
    setSyncing(true);
    try {
      await apiAdmin.captcha.syncHostnames(tenantId);
      snack.success(t('captcha.hostnamesSync.synced', 'Turnstile hostnames synced.'));
      onSynced();
    } catch (err) {
      const friendly = mapCaptchaSyncError(err, t);
      if (friendly) {
        snack.error(friendly);
      } else if (err instanceof ApiError) {
        snack.error(t(`errors.codes.${err.code}`, err.details.message ?? err.code));
      }
    } finally {
      setSyncing(false);
    }
  }

  const baseSyncButton = (
    <Button
      variant="outlined"
      onClick={() => void handleSync()}
      disabled={!provisioned || syncing}
      startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : null}
    >
      {t('captcha.hostnamesSync.button', 'Sync Cloudflare')}
    </Button>
  );

  const syncButton = provisioned ? (
    baseSyncButton
  ) : (
    <Tooltip
      title={t(
        'captcha.hostnamesSync.notProvisionedTooltip',
        'No Turnstile widget provisioned for this tenant.',
      )}
    >
      <span>{baseSyncButton}</span>
    </Tooltip>
  );

  return { syncButton };
}

// The captcha automation error codes (WIDGET_NOT_PROVISIONED / TOO_MANY_HOSTNAMES)
// are not in the repo's `ErrorCode` union, so `err.code` normalizes to UNKNOWN;
// the raw backend code is preserved on `err.details.raw.error.code` (U1's client).
function mapCaptchaSyncError(err: unknown, t: (k: string, f?: string) => string): string | null {
  if (!(err instanceof ApiError)) return null;
  const raw = err.details.raw as { error?: { code?: string } } | null | undefined;
  switch (raw?.error?.code) {
    case 'WIDGET_NOT_PROVISIONED':
      return t(
        'captcha.errors.widgetNotProvisioned',
        'No Turnstile widget is provisioned for this tenant yet. Provision a widget first.',
      );
    case 'TOO_MANY_HOSTNAMES':
      return t(
        'captcha.errors.tooManyHostnames',
        'This tenant has more than 10 APP/LOGIN hostnames — a Turnstile widget supports at most 10. Remove some hostnames and try again.',
      );
    default:
      return null;
  }
}

// The backend supplies a per-row `reason`; fall back to a derived message so the
// blocking list always reads sensibly even if `reason` is absent.
function blockingReason(h: RoutingReadinessHostname, t: (k: string, f?: string) => string): string {
  if (h.reason) return h.reason;
  if (!h.dnsVerified) return t('routing.deploy.reasonDnsNotVerified', 'DNS not verified');
  if (h.type === 'APP' && !h.appAssigned) return t('routing.deploy.reasonNoApp', 'No app assigned');
  return t('routing.deploy.reasonDnsNotVerified', 'DNS not verified');
}
